import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Stack,
  Paper,
  Chip,
  FormControlLabel,
  Checkbox,
  TextField,
  InputAdornment,
  Divider,
  Alert
} from '@mui/material';
import {
  FileText,
  Search,
  Check,
  AlertTriangle,
  FolderKanban,
  Tag
} from 'lucide-react';

function TemplateSelector({ open, onClose, onApply, submissionId, projectTypeId }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [applying, setApplying] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      const token = localStorage.getItem('portalToken');
      // Filter by project type if provided
      let url = '/api/templates';
      if (projectTypeId) {
        url += `?projectTypeId=${projectTypeId}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Only show active templates
        setTemplates(data.filter(t => t.isActive));
      }
    } catch (err) {
      console.error('Fetch templates error:', err);
    }
    setLoading(false);
  }, [projectTypeId]);

  useEffect(() => {
    if (open) {
      setLoading(true);
      fetchTemplates();
      setSelectedTemplate(null);
      setReplaceExisting(false);
      setSearchTerm('');
    }
  }, [open, fetchTemplates]);

  const handleApply = async () => {
    if (!selectedTemplate) return;
    
    setApplying(true);
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/templates/${selectedTemplate.id}/apply/${submissionId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ replaceExisting })
      });

      if (response.ok) {
        onApply();
        onClose();
      } else {
        const error = await response.json();
        alert(error.error || 'Erreur lors de l\'application du template');
      }
    } catch (err) {
      console.error('Apply template error:', err);
    }
    setApplying(false);
  };

  const calculateTemplateStats = (template) => {
    let totalItems = 0;
    let totalHours = 0;
    const categories = [];

    template.sections?.forEach(section => {
      // Collect categories
      if (section.category) {
        categories.push(section.category);
      } else if (section.customCategory) {
        categories.push({ name: section.customCategory, slug: 'custom' });
      }
      
      totalItems += section.items?.length || 0;
      section.items?.forEach(item => {
        // Calculate dynamic hours
        if (item.hours && item.hours.length > 0) {
          totalHours += item.hours.reduce((sum, h) => sum + (h.defaultHours || 0), 0);
        }
      });
    });

    return {
      sections: template.sections?.length || 0,
      items: totalItems,
      hours: totalHours,
      categories
    };
  };

  const filteredTemplates = templates.filter(t =>
    t.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none' } }}
    >
      <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
        <FileText size={24} color="#00d4aa" />
        Appliquer un template
      </DialogTitle>
      
      <DialogContent>
        {/* Search */}
        <TextField
          fullWidth
          size="small"
          placeholder="Rechercher un template..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} />
              </InputAdornment>
            )
          }}
          sx={{ mb: 2 }}
        />

        {/* Template List */}
        {loading ? (
          <Typography color="text.secondary">Chargement...</Typography>
        ) : filteredTemplates.length > 0 ? (
          <Stack spacing={1.5} sx={{ maxHeight: 400, overflowY: 'auto' }}>
            {filteredTemplates.map((template) => {
              const stats = calculateTemplateStats(template);
              const isSelected = selectedTemplate?.id === template.id;
              const templateColor = template.projectType?.color || '#00d4aa';

              return (
                <Paper
                  key={template.id}
                  onClick={() => setSelectedTemplate(template)}
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    bgcolor: isSelected ? `${templateColor}12` : 'rgba(255, 255, 255, 0.02)',
                    border: isSelected ? `2px solid ${templateColor}` : '1px solid rgba(255, 255, 255, 0.06)',
                    borderRadius: 2,
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: isSelected ? `${templateColor}18` : 'rgba(255, 255, 255, 0.04)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    {/* Selection indicator */}
                    <Box sx={{ 
                      width: 24, 
                      height: 24, 
                      borderRadius: '50%', 
                      border: isSelected ? `2px solid ${templateColor}` : '2px solid rgba(255, 255, 255, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: isSelected ? templateColor : 'transparent',
                      flexShrink: 0,
                      mt: 0.5
                    }}>
                      {isSelected && <Check size={14} color="#0a0a0f" />}
                    </Box>

                    <Box sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {template.name}
                        </Typography>
                        {template.projectType && (
                          <Chip
                            icon={<FolderKanban size={10} />}
                            label={template.projectType.name}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              bgcolor: `${templateColor}15`,
                              color: templateColor,
                              '& .MuiChip-icon': { color: templateColor }
                            }}
                          />
                        )}
                      </Box>
                      
                      {template.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                          {template.description}
                        </Typography>
                      )}

                      {/* Stats */}
                      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                        <Typography variant="caption" color="text.secondary">
                          {stats.sections} sections • {stats.items} éléments • {stats.hours}h
                        </Typography>
                        {stats.categories.length > 0 && (
                          <Stack direction="row" spacing={0.5}>
                            {stats.categories.slice(0, 3).map((cat, idx) => (
                              <Chip
                                key={idx}
                                icon={<Tag size={8} />}
                                label={cat.name}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.6rem',
                                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                                  '& .MuiChip-icon': { fontSize: 8 }
                                }}
                              />
                            ))}
                            {stats.categories.length > 3 && (
                              <Typography variant="caption" color="text.secondary">
                                +{stats.categories.length - 3}
                              </Typography>
                            )}
                          </Stack>
                        )}
                      </Stack>
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Stack>
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center', border: '1px dashed rgba(255, 255, 255, 0.1)' }}>
            {projectTypeId ? (
              <Alert severity="info" sx={{ justifyContent: 'center' }}>
                Aucun template disponible pour ce type de projet.
                Créez-en un depuis la page Templates.
              </Alert>
            ) : (
              <Typography color="text.secondary">
                {searchTerm ? 'Aucun template trouvé' : 'Aucun template disponible'}
              </Typography>
            )}
          </Paper>
        )}

        {/* Options */}
        {selectedTemplate && (
          <>
            <Divider sx={{ my: 2, opacity: 0.1 }} />
            <Paper sx={{ 
              p: 2, 
              bgcolor: 'rgba(245, 158, 11, 0.05)', 
              border: '1px solid rgba(245, 158, 11, 0.2)',
              borderRadius: 1
            }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={replaceExisting}
                    onChange={(e) => setReplaceExisting(e.target.checked)}
                    color="warning"
                  />
                }
                label={
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AlertTriangle size={16} color="#f59e0b" />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Remplacer les sections existantes
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Si coché, toutes les sections actuelles seront supprimées avant d'appliquer le template
                    </Typography>
                  </Box>
                }
              />
            </Paper>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} color="inherit">
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={!selectedTemplate || applying}
        >
          {applying ? 'Application...' : 'Appliquer le template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default TemplateSelector;
